import React from 'react';
import styled from 'styled-components';
import PageBanner from '../components/common/PageBanner';
import ContentSection from '../components/common/ContentSection';
import SectionTitle from '../components/common/SectionTitle';
import Button from '../components/common/Button';
import { FaBarcode, FaQrcode, FaLayerGroup, FaMobileAlt, FaShieldAlt } from 'react-icons/fa';
import { theme } from '../styles/GlobalStyles';

const StepsContainer = styled.div`
  margin-top: 4rem;
`;

const Step = styled.div`
  display: grid;
  grid-template-columns: 80px 1fr;
  gap: 2rem;
  margin-bottom: 3.5rem;
  position: relative;

  &:not(:last-child)::after {
    content: "";
    position: absolute;
    top: 80px;
    left: 40px;
    height: calc(100% + 3.5rem - 80px);
    width: 2px;
    background: linear-gradient(to bottom, rgba(97,93,255,0.4), rgba(61,217,235,0.15));
  }

  @media (max-width: 768px) { grid-template-columns: 64px 1fr; gap: 1.25rem; }
`;

const StepNumber = styled.div`
  width: 80px; height: 80px;
  border-radius: 50%;
  background: ${theme.gradient};
  color: white;
  font-size: 2rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0 30px rgba(97,93,255,0.4);
  flex-shrink: 0;

  @media (max-width: 768px) { width: 64px; height: 64px; font-size: 1.6rem; }
`;

const StepContent = styled.div`
  padding-top: 0.5rem;

  h3 {
    font-size: 1.6rem;
    color: ${theme.text};
    margin-bottom: 0.75rem;
  }

  p {
    color: ${theme.textMuted};
    line-height: 1.7;
    font-size: 1rem;
    max-width: 80%;
    @media (max-width: 992px) { max-width: 100%; }
  }
`;

const StepIcon = styled.div`
  font-size: 2.5rem;
  margin: 0.75rem 0;
  background: ${theme.gradient};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const DemoSection = styled.div`
  margin-top: 6rem;
  background: ${theme.cardBg};
  border: 1px solid ${theme.border};
  border-radius: 20px;
  padding: 3.5rem;
  text-align: center;
  backdrop-filter: blur(14px);

  p { color: ${theme.textMuted}; font-size: 1.05rem; margin-bottom: 0; }
`;

const VideoContainer = styled.div`
  max-width: 780px;
  margin: 2.5rem auto;
  border-radius: 14px;
  overflow: hidden;
  position: relative;
  padding-bottom: 56.25%;
  height: 0;
  border: 1px solid ${theme.border};

  img {
    position: absolute;
    top: 0; left: 0;
    width: 100%; height: 100%;
    object-fit: cover;
  }

  .play-button {
    position: absolute;
    top: 50%; left: 50%;
    transform: translate(-50%, -50%);
    width: 70px; height: 70px;
    background: rgba(97,93,255,0.85);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s;
    backdrop-filter: blur(8px);
    box-shadow: 0 0 30px rgba(97,93,255,0.5);

    &:hover { background: rgba(97,93,255,1); transform: translate(-50%, -50%) scale(1.08); }

    &::before {
      content: "";
      width: 0; height: 0;
      border-top: 12px solid transparent;
      border-bottom: 12px solid transparent;
      border-left: 20px solid white;
      margin-left: 4px;
    }
  }
`;

const FAQSection = styled.div`
  margin-top: 5rem;
`;

const FAQContainer = styled.div`
  margin-top: 3rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FAQItem = styled.div`
  background: ${theme.cardBg};
  border: 1px solid ${theme.border};
  border-radius: 14px;
  padding: 1.75rem;
  backdrop-filter: blur(10px);
  transition: all 0.25s ease;

  &:hover {
    border-color: rgba(97,93,255,0.3);
    background: ${theme.cardBgHover};
  }

  h4 {
    font-size: 1.05rem;
    color: ${theme.text};
    margin-bottom: 0.75rem;
    font-weight: 600;
  }

  p {
    color: ${theme.textMuted};
    line-height: 1.65;
    margin: 0;
    font-size: 0.92rem;
  }
`;

const DemoButtonWrapper = styled.div`
  margin-top: 2.5rem;
`;

const steps = [
  { num: 1, icon: <FaBarcode />,    title: 'Brand Registration',   desc: 'The brand registers products in the Prodseal platform individually or in batches. The system creates a secure product identity for each item.' },
  { num: 2, icon: <FaQrcode />,     title: 'QR Code Generation',   desc: 'Prodseal generates unique, secure QR codes for each registered product with encrypted information that counterfeiters cannot replicate.' },
  { num: 3, icon: <FaLayerGroup />, title: 'Code Application',     desc: 'QR codes are applied to products or packaging during manufacturing, with optional scratch-off protection layers for added security.' },
  { num: 4, icon: <FaMobileAlt />,  title: 'Consumer Verification',desc: 'Consumers scan the QR code with their smartphone. No special app required — the scan directs to a branded verification page.' },
  { num: 5, icon: <FaShieldAlt />,  title: 'Instant Results & Warranty', desc: 'Within seconds, the consumer receives an authentication result. For genuine products, warranty activation is offered in the same flow.' },
];

const faqs = [
  { q: 'How secure are the QR codes?', a: 'Prodseal QR codes contain encrypted data that cannot be duplicated or reverse-engineered. Each code is unique and tied to a specific product identity in our secure cloud database.' },
  { q: 'Can Prodseal integrate with our existing systems?', a: 'Yes, Prodseal offers robust API integration capabilities with ERP systems, production management software, warranty platforms, and CRM solutions.' },
  { q: 'How quickly can we implement Prodseal?', a: 'Most clients can begin protecting products within 2–4 weeks. This includes system setup, integration, code generation, and team training.' },
  { q: 'What happens if a consumer scans a counterfeit product?', a: "If a consumer scans a fake product, they'll receive a clear \"Suspected Counterfeit\" message. The brand is immediately notified, including location data to help identify counterfeit hotspots." },
  { q: 'Do consumers need to download an app to verify products?', a: "No. Prodseal is designed to be frictionless. Consumers use their smartphone's native camera app to scan the QR code, directed to a mobile-optimised verification page — no app download required." },
];

const HowItWorksPage = () => (
  <>
    <PageBanner
      title="How Prodseal Works"
      subtitle="Simple, secure, and scalable product authentication in 5 easy steps."
    />

    <ContentSection>
      <SectionTitle>A Simple Process for Powerful Protection</SectionTitle>

      <StepsContainer>
        {steps.map(({ num, icon, title, desc }) => (
          <Step key={num}>
            <StepNumber>{num}</StepNumber>
            <StepContent>
              <h3>{title}</h3>
              <StepIcon>{icon}</StepIcon>
              <p>{desc}</p>
            </StepContent>
          </Step>
        ))}
      </StepsContainer>

      <DemoSection>
        <SectionTitle>See Prodseal in Action</SectionTitle>
        <p>Watch our quick demo to see how simple and effective product authentication can be.</p>
        <VideoContainer>
          <img
            src="https://images.unsplash.com/photo-1563986768609-322da13575f3?ixlib=rb-4.0.3&auto=format&fit=crop&w=1170&q=80"
            alt="Prodseal Demo Video"
          />
          <div className="play-button" />
        </VideoContainer>
        <DemoButtonWrapper>
          <Button primary to="/contact">Schedule a Live Demo</Button>
        </DemoButtonWrapper>
      </DemoSection>

      <FAQSection>
        <SectionTitle>Frequently Asked Questions</SectionTitle>
        <FAQContainer>
          {faqs.map(({ q, a }) => (
            <FAQItem key={q}>
              <h4>{q}</h4>
              <p>{a}</p>
            </FAQItem>
          ))}
        </FAQContainer>
      </FAQSection>
    </ContentSection>
  </>
);

export default HowItWorksPage;
